import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";

interface UseUnreadMessagesOptions {
  isAdmin?: boolean;
}

export function useUnreadMessages({ isAdmin = false }: UseUnreadMessagesOptions = {}) {
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(true);

  const fetchUnreadCount = async () => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.user) {
        setUnreadCount(0);
        setLoading(false);
        return;
      }
      const user = session.user;

      if (isAdmin) {
        // For admins, count unread messages from support conversations
        const { data: conversations } = await supabase
          .from("conversations")
          .select("id, last_message_at")
          .in("type", ["support", "claim_inquiry"])
          .in("status", ["open", "in_progress"]);

        if (!conversations || conversations.length === 0) {
          setUnreadCount(0);
          setLoading(false);
          return;
        }

        // Get admin's participation records to check last_read_at
        const { data: participations } = await supabase
          .from("conversation_participants")
          .select("conversation_id, last_read_at")
          .eq("user_id", user.id);

        const participationMap = new Map(
          (participations || []).map(p => [p.conversation_id, p.last_read_at])
        );

        let totalUnread = 0;

        for (const conv of conversations) {
          const lastRead = participationMap.get(conv.id);
          
          let query = supabase
            .from("messages")
            .select("id", { count: "exact", head: true })
            .eq("conversation_id", conv.id)
            .neq("sender_id", user.id);

          if (lastRead) {
            query = query.gt("created_at", lastRead);
          }

          const { count } = await query;
          totalUnread += count || 0;
        }

        setUnreadCount(totalUnread);
      } else {
        // For regular users, count their own unread messages
        const { data: participations } = await supabase
          .from("conversation_participants")
          .select("conversation_id, last_read_at")
          .eq("user_id", user.id);

        if (!participations || participations.length === 0) {
          setUnreadCount(0);
          setLoading(false);
          return;
        }

        let totalUnread = 0;

        for (const participation of participations) {
          let query = supabase
            .from("messages")
            .select("id", { count: "exact", head: true })
            .eq("conversation_id", participation.conversation_id)
            .neq("sender_id", user.id);

          if (participation.last_read_at) {
            query = query.gt("created_at", participation.last_read_at);
          }

          const { count } = await query;
          totalUnread += count || 0;
        }

        setUnreadCount(totalUnread);
      }
    } catch (error) {
      console.error("Error fetching unread messages:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUnreadCount();

    // Set up real-time subscription
    const channel = supabase
      .channel(`unread-messages-${isAdmin ? "admin" : "user"}`)
      .on(
        "postgres_changes",
        {
          event: "INSERT",
          schema: "public",
          table: "messages",
        },
        () => {
          fetchUnreadCount();
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [isAdmin]);

  return { unreadCount, loading, refetch: fetchUnreadCount };
}